import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import {
	Column,
	TableState,
	useExpanded,
	useFlexLayout,
	useGroupBy,
	useRowSelect,
	useSortBy,
	useTable
} from 'react-table';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListOnScrollProps, VariableSizeList as List } from 'react-window';
import ReactTooltip from 'react-tooltip';
import Checkbox from '@bringg/react-components/dist/components/checkbox/checkbox';

import type { UpdatingSource } from '../types';
import { GroupedRow } from './table-components/';
import useStickyRow from './hooks/useStickyRow';
import { useGroupSelect } from './plugin-hooks/useGroupSelect';
import useTableControlledState from './hooks/useTableControlledState';
import useRenderRow from './hooks/useRenderRow';
import type { RoutesTableTranslations } from '../../components/routes-planner/translations';
import useStore from '../../stores/helpers/use-context';
import SummaryDetails from './summary-details/summary-details';
import EmptyMessage from './table-components/empty-message/empty-message';
import type { RoutesPlannerTask } from '../../types';
import PlannerTooltip from '../planner-tooltip/planner-tooltip';
import MainDetails from '../../components/main-details/main-details';
import RouteHeader from './table-components/route-header/route-header';
import { TABLE_COLUMNS } from './table-columns';

import './planner-table.scss';

interface IPlannerTableProps {
	columns: Column<RoutesPlannerTask>[];
	tasks: RoutesPlannerTask[];
	onToggleRuns: (runsIds: number[], show: boolean) => void;
	selectedRuns: Set<number>;
	totalRuns?: number;
	skipPageReset?: boolean;
	translations: RoutesTableTranslations;
	onMouseEnter?: (taskId: number) => void;
	onMouseLeave?: () => void;
	changeTasksSelection: (tasksIds: number[], source: UpdatingSource) => void;
}

export const TABLE_ROW_CLASS = 'table-row';
export const SELECTION_COLUMN = 'selection';
export const TABLE_ROW_HEIGHT = 34;
export const TABLE_ROW_HEADER_HEIGHT = TABLE_ROW_HEIGHT + 30;

export const IndeterminateCheckbox = React.forwardRef<HTMLInputElement, any>(({ indeterminate, ...rest }, ref) => {
	return <Checkbox indeterminate={indeterminate} {...rest} ref={ref} />;
});

const PlannerTable: React.FC<IPlannerTableProps> = props => {
	const { columns, tasks, onToggleRuns, selectedRuns, skipPageReset, translations, onMouseEnter, onMouseLeave } =
		props;

	const {
		tableStore,
		driversCount,
		routesCount,
		vehiclesCount,
		onContactSupport,
		shouldShowVehicles,
		shouldShowTrailer
	} = useStore();
	const listRef = useRef<List>(null);
	const bodyRef = useRef<HTMLDivElement>(null);

	const defaultColumn = useMemo(
		() => ({
			Cell: ({ value }) => {
				return <span>{value}</span>;
			}
		}),
		[]
	);

	useEffect(() => {
		if (listRef.current && skipPageReset === false) {
			listRef.current.resetAfterIndex(0);
		}
	}, [skipPageReset, tasks]);

	useEffect(() => {
		ReactTooltip.rebuild();
	}, [selectedRuns]);

	const hiddenColumns = useMemo(() => {
		const columnsIds = ['user_id', 'run_id'];
		Object.keys(TABLE_COLUMNS).forEach(columnId => {
			if (!tableStore.presentedColumns[columnId]) {
				columnsIds.push(columnId);
			}
		});
		return columnsIds;
	}, [tableStore.presentedColumns]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		totalColumnsWidth,
		isAllRowsExpanded,
		toggleAllRowsExpanded,
		state
	} = useTable(
		{
			data: tasks,
			columns,
			defaultColumn,
			initialState: {
				hiddenColumns: hiddenColumns,
				sortBy: [{ id: 'run_id' }, { id: 'priority' }],
				groupBy: ['run_id']
			},
			useControlledState: (state: TableState<RoutesPlannerTask>) => {
				return useTableControlledState(state, selectedRuns);
			},
			getRowId: ({ id }) => `${id}`,
			autoResetSortBy: !skipPageReset,
			autoResetGroupBy: !skipPageReset,
			autoResetExpanded: !skipPageReset
		},
		useGroupBy,
		useSortBy,
		useExpanded,
		useFlexLayout,
		useRowSelect,
		useGroupSelect
	);

	const getItemSize = useCallback(
		(index: number): number => {
			return !rows[index]?.isGrouped && rows[index - 1]?.isGrouped ? TABLE_ROW_HEADER_HEIGHT : TABLE_ROW_HEIGHT;
		},
		[rows]
	);

	const { stickyIndex, onStickyExpand, calculateSticky, clearSticky } = useStickyRow({
		listRef,
		bodyRef,
		rows,
		groupByState: state.groupBy,
		expandedState: state.expanded
	});

	const handleCollapseAllToggle = useCallback(() => {
		clearSticky();
		toggleAllRowsExpanded();
		listRef.current?.resetAfterIndex(0);
		listRef.current?.scrollTo(0);
	}, [listRef]);

	const handleGroupExpand = useCallback(
		(rowIndex: number) => {
			listRef.current?.resetAfterIndex(rowIndex);
		},
		[listRef]
	);

	const onDragStart = (id: number) => {
		tableStore.setTaskIdOfActiveDraggableItem(id);
	};

	const onDragEnd = () => {
		tableStore.setTaskIdOfActiveDraggableItem(null);
	};

	const RenderRow = useRenderRow(
		rows,
		prepareRow,
		totalColumnsWidth,
		state.expanded,
		onToggleRuns,
		handleGroupExpand,
		headerGroups,
		onDragStart,
		onDragEnd,
		translations,
		onMouseEnter,
		onMouseLeave
	);

	const RenderStickyRow = () => {
		const row = rows[stickyIndex];
		prepareRow(row);

		if (row.isGrouped) {
			return (
				<GroupedRow
					row={row}
					isSticky={true}
					rowIndex={stickyIndex}
					onExpand={onStickyExpand}
					onSelect={onToggleRuns}
				/>
			);
		}
	};

	const handleTableScroll = (props: ListOnScrollProps) => {
		Object.keys(state.expanded).length >= state.groupBy.length && calculateSticky(props.scrollDirection);
	};

	const ref = useRef(null);

	const tableWidth = useMemo(() => {
		return Math.max(totalColumnsWidth || 0, ref.current?.clientWidth || 0);
	}, [totalColumnsWidth, ref.current]);

	return (
		<div className="planner-table">
			<div className="planner-table-body">
				<MainDetails />
				<SummaryDetails
					rows={rows}
					tasksCount={tasks.length}
					driversCount={driversCount}
					routesCount={routesCount}
					vehicles={{
						enabled: shouldShowVehicles,
						count: vehiclesCount
					}}
					translations={translations.summaryDetails}
				/>
				<div className="line-separator" />
				{rows.length ? (
					<div className="table-container">
						<div className="auto-sizer-container">
							<AutoSizer>
								{({ height, width }) => (
									<div style={{ width }} ref={bodyRef}>
										<div className="table" {...getTableProps()}>
											<RouteHeader
												ref={ref}
												allRowExpanded={isAllRowsExpanded}
												onAllCollapseToggle={handleCollapseAllToggle}
												translation={translations.headers}
												shouldShowVehicles={shouldShowVehicles}
												shouldShowTrailer={shouldShowTrailer}
											/>
											<div className="table-body" style={{ width: tableWidth }}>
												{stickyIndex != null && RenderStickyRow()}
												<div style={{ width }}>
													<List
														ref={listRef}
														height={height - TABLE_ROW_HEIGHT}
														itemCount={rows.length}
														itemSize={getItemSize}
														width={tableWidth}
														overscanCount={20}
														innerElementType={({ children, style, ...rest }) => (
															<div {...getTableBodyProps()} {...rest} style={style}>
																{children}
															</div>
														)}
														className="planner-table-list"
														onScroll={handleTableScroll}
														onItemsRendered={() => {
															ReactTooltip.rebuild();
														}}
													>
														{RenderRow}
													</List>
												</div>
											</div>
											<PlannerTooltip
												id="drag-to-assign"
												arrowColor="transparent"
											></PlannerTooltip>
										</div>
									</div>
								)}
							</AutoSizer>
						</div>
					</div>
				) : (
					<EmptyMessage translations={translations.emptyTable} onContactSupport={onContactSupport} />
				)}
			</div>
		</div>
	);
};

export default PlannerTable;
